/**
 * Shared transport for the Log Review (SIRAR) service.
 *
 * Log Review lives behind its own backend service, so it gets its own thin
 * client rather than reusing the main API helper — but it keeps the same
 * contract as `frameworksApi`: bearer auth from the auth store, one shared
 * de-bounced session-expiry handler, and sanitized error messages.
 *
 * Every call accepts an `AbortSignal`; the screens fire overlapping chart
 * batches as filters change and abort the stale ones.
 */

import { briefHttpError, getAuthHeaders, handleSessionExpiration } from "./frameworksApi";

/** Throw a rego-shaped error (name = title, message = detail) from a failed response. */
async function throwHttpError(res: Response, path: string): Promise<never> {
  const body = await res.text().catch(() => "");
  let title = "Request Failed";
  let detail = briefHttpError(res.status, body, res.statusText);
  try {
    const parsed = JSON.parse(body);
    const firstError = parsed?.errors?.[0] || parsed;
    if (firstError?.title || firstError?.detail) {
      title = firstError.title || title;
      detail = firstError.detail || firstError.message || detail;
    }
  } catch {
    // body is not JSON — keep the brief message from briefHttpError above
  }
  const err = new Error(detail);
  err.name = title;
  (err as Error & { status?: number; path?: string }).status = res.status;
  (err as Error & { status?: number; path?: string }).path = path;
  throw err;
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, init);

  if (res.status === 401) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }
  if (!res.ok) await throwHttpError(res, path);

  // 204 No Content — nothing to parse
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const logReviewGet = <T>(path: string, signal?: AbortSignal): Promise<T> =>
  request<T>(path, {
    method: "GET",
    headers: { Accept: "application/json", ...getAuthHeaders() },
    signal,
  });

export const logReviewPost = <T>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<T> =>
  request<T>(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });

/** Multipart POST — Content-Type is left unset so the browser adds the boundary. */
export const logReviewPostForm = <T>(
  path: string,
  form: FormData,
  signal?: AbortSignal,
): Promise<T> =>
  request<T>(path, {
    method: "POST",
    headers: { Accept: "application/json", ...getAuthHeaders() },
    body: form,
    signal,
  });

// ─── Shared parsing helpers ─────────────────────────────────────────────

/** Coerce a backend number-or-numeric-string to a finite number (0 otherwise). */
export const toNumber = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};


/**
 * Standard response envelope for the write endpoints (manage compliance,
 * review decisions). Success is decided by `statusCodeValue` (2xx), with
 * `body` surfaced to the user as a toast message.
 */
export interface LogReviewWriteResponse {
  body?: string;
  statusCodeValue?: number;
  statusCode?: string;
}

export interface LogReviewWriteResult {
  /** Server-supplied message surfaced to the user via a toast. */
  message: string;
  /** True when `statusCodeValue` is a 2xx (e.g. 202 ACCEPTED). */
  success: boolean;
}

export const toWriteResult = (raw: LogReviewWriteResponse): LogReviewWriteResult => {
  const code = raw.statusCodeValue ?? 0;
  return { message: raw.body ?? "", success: code >= 200 && code < 300 };
};
